/**
 * YawmiModule — minimal native module implementing the PreviewableAweniModule
 * contract from `src/modules/types.ts`.
 *
 * v0 (this file):
 *   - `preview()` returns a placeholder snapshot ("Daily check") so the
 *     AWENI home Today zone has a Yawmi card. Real metrics (habits done /
 *     streak / next slot) come from C6 when the calendar/scheduler ships.
 *   - `mount()` renders a "coming soon" surface that explains the scope
 *     (Yawmi vs Memoriz boundary doc) and links to the home.
 *
 * v1 (Codex C6):
 *   - Calendar grid (month view) consuming HijriCalendar + national holidays.
 *   - Habit tracker reading cross-module activity events via SDK.
 *   - Wishing-card composer + RDV scheduler.
 */

import type {
  PreviewableAweniModule,
  PreviewSnapshot,
  MountedInstance,
  ModuleRoute,
} from '../types';
import type { AweniContext } from '../../types';

const ROUTES: ModuleRoute[] = [
  { path: '/yawmi', primary: true },
  { path: '/yawmi/calendar' },
  { path: '/yawmi/habits' },
];

let mountSeq = 0;
const SNAPSHOT_CACHE_KEY = 'aweni_preview_yawmi';

export class YawmiModule implements PreviewableAweniModule {
  readonly id = 'yawmi';
  readonly routes = ROUTES;
  private container: HTMLElement | null = null;
  private currentMountId: string | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.currentMountId = `yawmi_${++mountSeq}`;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';
    container.innerHTML = `
      <div class="yawmi-shell" dir="${isRtl ? 'rtl' : 'ltr'}" style="max-width:600px;margin:0 auto;padding:32px 16px;color:var(--ln-text-primary);">
        <h1 style="font-size:1.75rem;margin:0 0 8px 0;color:var(--ln-text-primary);">${escHtml(t('door_yawmi') || 'Yawmi')}</h1>
        <p style="color:var(--ln-text-secondary);margin:0 0 24px 0;">${escHtml(t('yawmi_subtitle') || 'Your daily life in one room — calendar, habits, gentle reminders.')}</p>
        <div style="background:var(--ln-bg-card);border:1px solid var(--ln-border, rgba(0,0,0,0.08));border-radius:var(--ln-radius-lg,16px);padding:24px;">
          <div style="font-size:1.125rem;font-weight:600;margin-bottom:8px;">${escHtml(t('yawmi_coming_soon_title') || 'Coming soon')}</div>
          <p style="margin:0 0 16px 0;color:var(--ln-text-secondary);line-height:1.6;">
            ${escHtml(t('yawmi_coming_soon_body') || 'Yawmi is the daily-life scheduler for AWENI: calendar with Hijri uncertainty, habit tracker, wishing-card composer, gentle reminders. The first version will ship soon.')}
          </p>
          <a href="#/home" style="display:inline-block;padding:10px 20px;background:var(--ln-accent);color:var(--ln-accent-text);border-radius:var(--ln-radius-md,12px);text-decoration:none;font-weight:600;">
            ${escHtml(t('yawmi_back_home') || 'Back to home')}
          </a>
        </div>
      </div>
    `;
  }

  unmount(): void {
    if (this.container) this.container.innerHTML = '';
    this.container = null;
    this.currentMountId = null;
  }

  destroy(_instance: MountedInstance): void {
    this.unmount();
  }

  async preview(ctx: AweniContext): Promise<PreviewSnapshot> {
    try {
      const t = ctx.i18n.t.bind(ctx.i18n);
      const snapshot: PreviewSnapshot = {
        primaryMetric: {
          label: t('yawmi_preview_today_label') || 'Today',
          value: t('yawmi_preview_placeholder_value') || 'Daily check',
        },
        secondaryMetric: {
          label: '',
          value: t('yawmi_coming_soon_short') || 'Coming soon',
        },
        ctaLabel: t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: `Yawmi — ${t('yawmi_preview_placeholder_value') || 'Daily check'}`,
        bgVariant: 'neutral',
        computedAt: new Date().toISOString(),
      };
      try { localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot)); }
      catch { /* ignore */ }
      return snapshot;
    } catch {
      return {
        primaryMetric: { label: '', value: '' },
        ctaLabel: ctx.i18n.t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: 'Yawmi',
        errorState: {
          message: ctx.i18n.t('preview_tap_to_open') || 'Tap to open',
          recoveryRoute: '#/yawmi',
        },
        computedAt: new Date().toISOString(),
      };
    }
  }
}

function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

export default new YawmiModule();
